/**
 * Notice de succès après clic sur « Enregistrer » (GP Advanced Save & Continue).
 *
 * La confirmation inline native GP ASC ne fonctionne pas avec les formulaires AJAX :
 * on affiche donc un bandeau en haut du formulaire (là où GF scroll déjà).
 */

const NOTICE_ID_PREFIX = 'hf-gf-save-notice-';

const NOTICE_MESSAGE =
    'Vous venez de sauvegarder votre progression. Vous pouvez fermer cette page et continuer plus tard depuis votre espace privé, ou poursuivre dès maintenant.';

/** @type {Map<string, number>} formId → timestamp du clic save manuel */
const pendingManualSaves = new Map();

/**
 * @param {Element} el
 * @returns {string|null}
 */
function getFormIdFromElement(el) {
    const form = el.closest('form[id^="gform_"]') || el.closest('.gform_wrapper');
    if (!form) {
        return null;
    }

    const match = (form.id || '').match(/(\d+)/);
    return match ? match[1] : null;
}

/**
 * @param {string|number} formId
 * @returns {HTMLElement|null}
 */
function getFormWrapper(formId) {
    return (
        document.getElementById(`gform_wrapper_${formId}`) ||
        document.querySelector(`#gform_${formId}`)?.closest('.gform_wrapper') ||
        null
    );
}

/**
 * @param {string|number} formId
 */
function showSaveSuccessNotice(formId) {
    const wrapper = getFormWrapper(formId);
    if (!wrapper) {
        return;
    }

    const noticeId = `${NOTICE_ID_PREFIX}${formId}`;
    let notice = document.getElementById(noticeId);

    if (!notice) {
        notice = document.createElement('div');
        notice.id = noticeId;
        notice.className = 'hf-gf-save-notice';
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');

        notice.innerHTML = [
            '<p class="hf-gf-save-notice__text"></p>',
            '<button type="button" class="hf-gf-save-notice__close" aria-label="Fermer">&times;</button>',
        ].join('');

        notice.querySelector('.hf-gf-save-notice__text').textContent = NOTICE_MESSAGE;

        notice.querySelector('.hf-gf-save-notice__close').addEventListener('click', () => {
            notice.remove();
        });

        wrapper.insertBefore(notice, wrapper.firstChild);
    } else {
        notice.querySelector('.hf-gf-save-notice__text').textContent = NOTICE_MESSAGE;
        wrapper.insertBefore(notice, wrapper.firstChild);
    }

    notice.classList.remove('is-leaving');
    // Relance l'animation si on sauvegarde à nouveau
    notice.classList.remove('is-visible');
    // force reflow
    void notice.offsetWidth;
    notice.classList.add('is-visible');
}

/**
 * @param {string|number} formId
 * @param {boolean} [force]
 */
function completeManualSave(formId, force = false) {
    const key = String(formId);
    const clickedAt = pendingManualSaves.get(key);

    if (!force && !clickedAt) {
        return;
    }

    // Ignore les auto-saves trop éloignés d'un clic manuel (> 15s)
    if (!force && Date.now() - clickedAt > 15000) {
        pendingManualSaves.delete(key);
        return;
    }

    pendingManualSaves.delete(key);
    showSaveSuccessNotice(formId);
}

/**
 * Marque un save manuel en attente.
 *
 * @param {string|number} formId
 */
function markManualSave(formId) {
    pendingManualSaves.set(String(formId), Date.now());
}

function bindClickTracking() {
    document.addEventListener(
        'click',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const saveLink = target.closest(
                '.gform_save_link, a.gform_save_link, button.gform_save_link, input.gform_save_link'
            );

            if (!saveLink) {
                return;
            }

            const formId = getFormIdFromElement(saveLink);
            if (formId) {
                markManualSave(formId);
            }
        },
        true
    );
}

function bindGpascHooks() {
    const register = () => {
        if (!window.gform || typeof window.gform.addAction !== 'function') {
            return false;
        }

        if (window.gform.__hfSaveNoticeBound) {
            return true;
        }

        window.gform.__hfSaveNoticeBound = true;

        // Certaines configs GP ASC passent le bouton save par le même pipeline que l'auto-save.
        window.gform.addAction('gpasc_auto_save_finished', (formId, _gpasc, successInfo) => {
            const ok = !successInfo || successInfo.success !== false;
            if (ok) {
                completeManualSave(formId);
            } else {
                pendingManualSaves.delete(String(formId));
            }
        });

        return true;
    };

    if (register()) {
        return;
    }

    document.addEventListener('gform/theme/scripts_loaded', register);
    document.addEventListener('gform_main_scripts_loaded', register);

    let tries = 0;
    const timer = window.setInterval(() => {
        tries += 1;
        if (register() || tries > 40) {
            window.clearInterval(timer);
        }
    }, 250);
}

function bindAjaxFallback() {
    if (!window.jQuery) {
        return;
    }

    window.jQuery(document).ajaxComplete((_event, _xhr, settings) => {
        if (!pendingManualSaves.size) {
            return;
        }

        const data = settings && settings.data != null ? String(settings.data) : '';
        const url = settings && settings.url != null ? String(settings.url) : '';
        const haystack = `${url} ${data}`;

        const looksLikeSave =
            haystack.includes('gform_save') ||
            haystack.includes('gf_save') ||
            haystack.includes('gpasc') ||
            haystack.includes('save_and_continue');

        if (!looksLikeSave) {
            return;
        }

        // Essaie d'extraire le form_id de la requête, sinon complète tous les pending.
        const match = haystack.match(/gform_submit[=_](\d+)/) || haystack.match(/form_id[=_](\d+)/);
        if (match) {
            completeManualSave(match[1]);
            return;
        }

        pendingManualSaves.forEach((_ts, formId) => {
            completeManualSave(formId);
        });
    });
}

/**
 * Filet de sécurité : disparition du spinner / fin de chargement du bouton save.
 */
function bindSpinnerFallback() {
    if (!window.jQuery) {
        return;
    }

    // Après un clic save, surveille quelques secondes la fin du spinner GF.
    document.addEventListener(
        'click',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const saveLink = target.closest('.gform_save_link');
            if (!saveLink) {
                return;
            }

            const formId = getFormIdFromElement(saveLink);
            if (!formId) {
                return;
            }

            let sawSpinner = false;
            let checks = 0;

            const timer = window.setInterval(() => {
                checks += 1;
                const spinner = document.getElementById(`gform_ajax_spinner_${formId}`);
                const visible =
                    spinner &&
                    window.getComputedStyle(spinner).display !== 'none' &&
                    !spinner.hasAttribute('hidden');

                if (visible) {
                    sawSpinner = true;
                }

                if (sawSpinner && !visible) {
                    window.clearInterval(timer);
                    completeManualSave(formId);
                    return;
                }

                // Au-delà de ~8s sans spinner vu/parti, on abandonne ce filet.
                if (checks > 40) {
                    window.clearInterval(timer);
                }
            }, 200);
        },
        true
    );
}

export function initGfSaveSuccessNotice() {
    if (document.body.dataset.hfGfSaveNotice === '1') {
        return;
    }

    document.body.dataset.hfGfSaveNotice = '1';

    bindClickTracking();
    bindGpascHooks();
    bindAjaxFallback();
    bindSpinnerFallback();
}
